import React from "react";
import "../styles/pages/about.scss";

const MARQUEE_ROW_1 = [
  "React.js",
  "Next.js",
  "TypeScript",
  "Go",
  "Python",
  "C++",
  "PyTorch",
  "Flutter",
  "Node.js",
  "Express",
  "PostgreSQL",
  "YOLOv5",
  "Mistral-7B",
  "Docker",
  "Tailwind CSS"
];

const MARQUEE_ROW_2 = [
  "Prisma",
  "Nuxt 3",
  "Vue.js",
  "Auth0",
  "AWS (EC2, S3)",
  "Firebase",
  "Nginx",
  "Linux",
  "Git",
  "SQL",
  "QLoRA (4-bit)",
  "Qt",
  "Computer Vision",
  "REST APIs",
  "Deep Learning"
];

function About() {
  return (
    <section id="about" className="anchor">
      {/* ================= ABOUT ================= */}
      <header className="section-header reveal-on-scroll">
        <span className="section-eyebrow">PROFILE</span>
        <h2>ABOUT ME</h2>
        <p className="section-subtitle">
          Full-Stack Developer & AI Builder crafting complete, robust applications from UI to distributed backends.
        </p>
      </header>

      <div className="bio-section glass-card reveal-on-scroll">
        <div className="intro-text">
          <h3>Hi, I'm Pratyush Kumar Rai.</h3>
          <p className="tagline">Full-Stack Developer · AI/ML Engineer · Defense Systems Builder</p>
        </div>

        <p>
          I am a passionate Software Developer with a primary focus on Full
          Stack Development. I love the process of building complete,
          end-to-end applications—from designing intuitive, performant user interfaces to
          engineering the robust backends and distributed systems that power them.
        </p>
        <p>
          Beyond traditional software, I have a deep interest in Artificial
          Intelligence and Machine Learning. I specialize in integrating computer vision,
          deep learning models, and large language model (LLM) workflows into real-world web and
          mobile platforms. My research in deep learning computer vision frameworks has been published in peer-reviewed proceedings with Elsevier / ScienceDirect.
        </p>
        <p>
          Having engineered mission-critical defense C++/Qt software and real-time TCP sonar parsing at Bharat Electronics Limited (BEL), I value rigorous code quality, maintainability, and clean architecture across every project I deliver.
        </p>
      </div>

      {/* ================= SKILLS ================= */}
      <header className="section-header reveal-on-scroll">
        <span className="section-eyebrow">CAPABILITIES</span>
        <h2>TECHNICAL SKILLS</h2>
        <p className="section-subtitle">
          Core languages, frameworks, cloud infrastructure, and AI toolsets.
        </p>
      </header>

      {/* DUAL INFINITE MARQUEE */}
      <div className="skills-marquee-container reveal-on-scroll" aria-label="Skills Highlights Marquee">
        <div className="marquee-track track-left">
          {[...MARQUEE_ROW_1, ...MARQUEE_ROW_1].map((skill, idx) => (
            <span key={`r1-${idx}`} className="marquee-chip">
              <span className="marquee-dot">●</span>
              {skill}
            </span>
          ))}
        </div>
        <div className="marquee-track track-right">
          {[...MARQUEE_ROW_2, ...MARQUEE_ROW_2].map((skill, idx) => (
            <span key={`r2-${idx}`} className="marquee-chip">
              <span className="marquee-dot">●</span>
              {skill}
            </span>
          ))}
        </div>
      </div>

      <div className="skills-section">
        {/* Languages */}
        <div className="skill-card glass-card reveal-on-scroll">
          <div className="skill-header">
            <span className="skill-icon-svg" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6"></polyline>
                <polyline points="8 6 2 12 8 18"></polyline>
              </svg>
            </span>
            <h4>Languages</h4>
          </div>
          <div className="skill-tags">
            <span>C</span>
            <span>C++</span>
            <span>Python</span>
            <span>Java</span>
            <span>JavaScript</span>
            <span>TypeScript</span>
            <span>Go</span>
            <span>SQL</span>
            <span>Dart</span>
          </div>
        </div>

        {/* Full-Stack & Web */}
        <div className="skill-card glass-card reveal-on-scroll">
          <div className="skill-header">
            <span className="skill-icon-svg" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </span>
            <h4>Full Stack & Frameworks</h4>
          </div>
          <div className="skill-tags">
            <span>ReactJS</span>
            <span>Next.js</span>
            <span>Nuxt 3</span>
            <span>Vue.js</span>
            <span>Flutter</span>
            <span>Node.js</span>
            <span>Express</span>
            <span>Prisma</span>
            <span>Tailwind CSS</span>
            <span>Qt</span>
          </div>
        </div>

        {/* AI & Machine Learning */}
        <div className="skill-card glass-card reveal-on-scroll">
          <div className="skill-header">
            <span className="skill-icon-svg" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a4 4 0 0 0-4 4c0 1.25.58 2.37 1.48 3.1A6 6 0 0 0 3 15v1a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1a6 6 0 0 0-6.48-5.9A4 4 0 0 0 16 6a4 4 0 0 0-4-4z"></path>
                <line x1="12" y1="12" x2="12" y2="16"></line>
              </svg>
            </span>
            <h4>AI & Machine Learning</h4>
          </div>
          <div className="skill-tags">
            <span>PyTorch</span>
            <span>Hugging Face</span>
            <span>YOLOv5</span>
            <span>Mistral-7B</span>
            <span>QLoRA (4-bit)</span>
            <span>Computer Vision</span>
            <span>Deep Learning</span>
            <span>Generative AI</span>
            <span>NLP</span>
          </div>
        </div>

        {/* Cloud, Systems & DevOps */}
        <div className="skill-card glass-card reveal-on-scroll">
          <div className="skill-header">
            <span className="skill-icon-svg" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                <line x1="6" y1="6" x2="6.01" y2="6"></line>
                <line x1="6" y1="18" x2="6.01" y2="18"></line>
              </svg>
            </span>
            <h4>Cloud, DevOps & Tools</h4>
          </div>
          <div className="skill-tags">
            <span>Docker</span>
            <span>Docker Compose</span>
            <span>Nginx</span>
            <span>PostgreSQL</span>
            <span>MySQL</span>
            <span>Auth0</span>
            <span>Firebase</span>
            <span>AWS (EC2, S3)</span>
            <span>Linux</span>
            <span>Git</span>
            <span>Google Colab</span>
            <span>Anaconda</span>
            <span>Arduino IDE</span>
          </div>
        </div>
      </div>

      {/* ================= EDUCATION ================= */}
      <header className="section-header reveal-on-scroll">
        <span className="section-eyebrow">ACADEMICS</span>
        <h2>EDUCATION</h2>
        <p className="section-subtitle">
          Foundational computer science education, engineering principles, and specialized coursework.
        </p>
      </header>

      <div className="education-card glass-card">
        <div className="education-top">
          <div className="education-icon-wrap" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
              <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
            </svg>
          </div>
          <div className="education-main">
            <h3 className="institution-name">Presidency University</h3>
            <p className="degree-title">B.Tech in Computer Engineering</p>
            <span className="education-duration">
              <i className="fa fa-calendar-o" aria-hidden="true"></i> Oct 2020 – Nov 2024
            </span>
          </div>
        </div>

        <div className="coursework-section">
          <h4 className="coursework-heading">Relevant Coursework:</h4>
          <div className="coursework-tags">
            <span>Artificial Intelligence</span>
            <span>Machine Learning</span>
            <span>Deep Learning</span>
            <span>Generative AI</span>
            <span>Natural Language Processing</span>
            <span>Computer Vision</span>
            <span>Data Structures & Algorithms</span>
            <span>Operating Systems</span>
            <span>Computer Networks</span>
            <span>Database Management Systems</span>
            <span>Cloud Computing</span>
            <span>Distributed Systems</span>
            <span>Web Technologies</span>
            <span>IoT</span>
            <span>Mobile Application Development</span>
          </div>
        </div>
      </div>

      {/* ================= CERTIFICATIONS ================= */}
      <header className="section-header reveal-on-scroll">
        <span className="section-eyebrow">CREDENTIALS</span>
        <h2>CERTIFICATIONS</h2>
        <p className="section-subtitle">
          Industry-recognized credentials in Artificial Intelligence, Software Engineering, and Database Architecture.
        </p>
      </header>

      <div className="certifications-grid">
        <div className="cert-card glass-card reveal-on-scroll">
          <div className="cert-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="7"></circle>
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
            </svg>
          </div>
          <div className="cert-content">
            <h4>Salesforce Certified AI Associate</h4>
            <span className="cert-issuer">Salesforce</span>
          </div>
        </div>

        <div className="cert-card glass-card reveal-on-scroll">
          <div className="cert-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="7"></circle>
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
            </svg>
          </div>
          <div className="cert-content">
            <h4>Salesforce Certified AI Specialist</h4>
            <span className="cert-issuer">Salesforce</span>
          </div>
        </div>

        <div className="cert-card glass-card reveal-on-scroll">
          <div className="cert-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <div className="cert-content">
            <h4>Software Engineering Job Simulation</h4>
            <span className="cert-issuer">J.P. Morgan</span>
          </div>
        </div>

        <div className="cert-card glass-card reveal-on-scroll">
          <div className="cert-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
          </div>
          <div className="cert-content">
            <h4>Introduction to Artificial Intelligence</h4>
            <span className="cert-issuer">IBM</span>
          </div>
        </div>

        <div className="cert-card glass-card reveal-on-scroll">
          <div className="cert-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
          </div>
          <div className="cert-content">
            <h4>SQL Basic & Intermediate</h4>
            <span className="cert-issuer">HackerRank</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
